import { defineStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/styled-system';

/**
 * Semantic button variants for the provider portal.
 * All variants reference semantic tokens — they adapt to color mode automatically.
 *
 * When colorScheme="branded" (the default), variants use semantic tokens.
 * When any other colorScheme is passed (e.g. "success", "orange"), variants
 * fall back to standard Chakra palette-based styling so that existing usages
 * like colorScheme="success" continue to work correctly.
 *
 * Variants:
 *  - solid:       Brand bg, inverse text (primary action) [DEFAULT]
 *  - outline:     Transparent bg, brand border & text (branded outline)
 *  - secondary:   Default bg, default text, default border — may be deprecated
 *  - danger:      Danger bg, inverse text (destructive actions)
 *  - ghost:       No bg/border, brand text (minimal / tertiary)
 *  - ghost-muted: No bg/border, muted text (de-emphasised tertiary)
 */

const baseStyle = {
  fontWeight: 'semibold',
  borderRadius: 'md',
};

const disabledStyle = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

// --- Variants ---

const variantSolid = (props: StyleFunctionProps) => {
  const { colorScheme: c } = props;
  if (c !== 'branded') {
    return {
      bg: `${c}.500`,
      color: 'white',
      _hover: { bg: `${c}.600` },
      _active: { bg: `${c}.700` },
      _disabled: disabledStyle,
    };
  }
  return {
    bg: 'interactive.brand.default',
    color: 'text.inverse',
    borderWidth: '1px',
    borderColor: 'interactive.brand.default',
    _hover: {
      bg: 'interactive.brand.hover',
      borderColor: 'interactive.brand.hover',
    },
    _active: {
      bg: 'interactive.brand.active',
      borderColor: 'interactive.brand.active',
    },
    _disabled: disabledStyle,
  };
};

const variantOutline = (props: StyleFunctionProps) => {
  const { colorScheme: c } = props;
  if (c !== 'branded') {
    return {
      bg: 'transparent',
      color: `${c}.500`,
      borderWidth: '1px',
      borderColor: `${c}.500`,
      _hover: { bg: `${c}.50` },
      _active: { bg: `${c}.100` },
      _disabled: disabledStyle,
    };
  }
  return {
    bg: 'transparent',
    color: 'text.brand',
    borderWidth: '1px',
    borderColor: 'border.brand',
    _hover: { bg: 'bg.brand' },
    _active: { bg: 'green.150' },
    _disabled: disabledStyle,
  };
};

/** @deprecated Consider using outline or ghost instead. */
const variantSecondary = {
  bg: 'bg.default',
  color: 'text.default',
  borderWidth: '1px',
  borderColor: 'border.subtle',
  _hover: {
    bg: 'bg.subtle',
    borderColor: 'border.muted',
  },
  _active: { bg: 'bg.muted' },
  _disabled: disabledStyle,
};

const variantDanger = {
  bg: 'interactive.danger.default',
  color: 'text.inverse',
  borderWidth: '1px',
  borderColor: 'interactive.danger.default',
  _hover: {
    bg: 'interactive.danger.hover',
    borderColor: 'interactive.danger.hover',
  },
  _active: { bg: 'interactive.danger.hover' },
  _disabled: disabledStyle,
};

const variantGhost = (props: StyleFunctionProps) => {
  const { colorScheme: c } = props;
  if (c !== 'branded') {
    return {
      bg: 'transparent',
      color: `${c}.500`,
      _hover: { bg: `${c}.50` },
      _active: { bg: `${c}.100` },
      _disabled: disabledStyle,
    };
  }
  return {
    bg: 'transparent',
    color: 'text.brand',
    _hover: { bg: 'bg.brand' },
    _active: { bg: 'green.150' },
    _disabled: disabledStyle,
  };
};

const variantGhostMuted = {
  bg: 'transparent',
  color: 'text.muted',
  _hover: { bg: 'bg.subtle' },
  _active: { bg: 'bg.muted' },
  _disabled: disabledStyle,
};

export const buttonStyleConfig = defineStyleConfig({
  baseStyle,
  variants: {
    solid: variantSolid,
    outline: variantOutline,
    /** @deprecated Consider using outline or ghost instead. */
    secondary: variantSecondary,
    danger: variantDanger,
    ghost: variantGhost,
    'ghost-muted': variantGhostMuted,
  },
  defaultProps: {
    variant: 'solid',
    size: 'md',
    colorScheme: 'branded',
  },
});
